import { useState, useRef, useEffect, FormEvent } from 'react';
import { chatWithOski } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { useAgentStatus } from '../../hooks/useAgentStatus';
import { useChatHistory } from '../../hooks/useChatHistory';
import { useAppConfigs } from '../../hooks/useAppConfigs';
import type { AgentName, AppName, AppRole, ChatMessage, OskiImageAttachment, Ticket } from '../../types';
import styles from './OskiChat.module.css';

const AGENT_META: Record<AgentName, { label: string; color: string; gradient: string }> = {
  oski:  { label: 'Oski · Orquestador', color: 'var(--broota)',  gradient: 'var(--oski-grad)'  },
  pame:  { label: 'Pame · Producto/Dev', color: 'var(--nalca)',   gradient: 'var(--pame-grad)'  },
  neo:   { label: 'Neo · Implementación', color: 'var(--xbroota)', gradient: 'var(--pame-grad)' },
  dully: { label: 'Dully · Legal',        color: 'var(--dully)',   gradient: 'var(--dully-grad)' },
};

const VISIBLE_AGENTS: AgentName[] = ['oski', 'pame', 'neo'];
const MAX_IMAGES = 3;

function msgId() {
  return Math.random().toString(36).slice(2);
}

function readFileAsAttachment(file: File): Promise<OskiImageAttachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      const [header, data] = result.split(',');
      const mediaType = (header.match(/data:([^;]+)/)?.[1] ?? 'image/jpeg') as OskiImageAttachment['mediaType'];
      resolve({ data, mediaType });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function OskiChat() {
  const { teamUser, isSysadmin }          = useAuth();
  const { status, checking }              = useAgentStatus();
  const { messages, setMessages, loadingHistory } = useChatHistory(teamUser?.uid);
  const [input, setInput]                 = useState('');
  const [loading, setLoading]             = useState(false);
  const [statusText, setStatus]           = useState('');
  const [activeAgent, setActiveAgent]           = useState<AgentName>('oski');
  const [pendingImages, setPendingImages]       = useState<OskiImageAttachment[]>([]);
  const [ticketCreated, setTicketCreated]       = useState<{ title: string; notionId: string } | null>(null);
  const [pendingSuggestedRequest, setPendingSuggestedRequest] = useState<Partial<Ticket> | null>(null);
  const [selectedApp, setSelectedApp]           = useState<string | null>(null);
  const { apps }                                = useAppConfigs();
  const bottomRef                         = useRef<HTMLDivElement>(null);
  const inputRef                          = useRef<HTMLTextAreaElement>(null);
  const fileInputRef                      = useRef<HTMLInputElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Escuchar evento de pre-llenado desde ticket rechazado
  useEffect(() => {
    const handler = (e: Event) => {
      const { message } = (e as CustomEvent).detail ?? {};
      if (message) {
        setTicketCreated(null);
        setInput(message);
        inputRef.current?.focus();
      }
    };
    window.addEventListener('oski-prefill', handler);
    return () => window.removeEventListener('oski-prefill', handler);
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).slice(0, MAX_IMAGES - pendingImages.length);
    if (!files.length) return;
    const attachments = await Promise.all(files.map(readFileAsAttachment));
    setPendingImages(prev => [...prev, ...attachments].slice(0, MAX_IMAGES));
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    setPendingImages(prev => prev.filter((_, i) => i !== index));
  };

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    const text = input.trim();
    if ((!text && !pendingImages.length) || loading) return;

    const imagesToSend = [...pendingImages];

    const userMsg: ChatMessage = {
      id:        msgId(),
      role:      'user',
      content:   text,
      timestamp: new Date().toISOString(),
      images:    imagesToSend.length ? imagesToSend : undefined,
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setPendingImages([]);
    setLoading(true);
    setStatus('Pensando...');

    try {
      const response = await chatWithOski(
        {
          message: text || '(imagen adjunta)',
          history: messages,
          userId:  teamUser?.uid ?? '',
          images:  imagesToSend.length ? imagesToSend : undefined,
          pendingSuggestedRequest: pendingSuggestedRequest ?? undefined,
          appContext: selectedApp ?? undefined,
        },
        (t) => setStatus(t)
      );

      const agentMsg: ChatMessage = {
        id:        msgId(),
        role:      'assistant',
        agent:     response.agent,
        content:   response.reply,
        timestamp: new Date().toISOString(),
      };

      setActiveAgent(response.agent);
      setMessages(prev => [...prev, agentMsg]);

      // Guardar suggestedRequest cuando Pame propone (confirmed=false), para pasarlo en la confirmación
      if (response.suggestedRequest && !response.createdTicket) {
        setPendingSuggestedRequest(response.suggestedRequest);
      }

      if (response.ticketError) {
        setMessages(prev => [...prev, {
          id:        msgId(),
          role:      'assistant',
          agent:     'oski',
          content:   `⚠️ ${response.ticketError} — el ticket no fue registrado en Notion. Podés intentarlo de nuevo o contactar a un admin.`,
          timestamp: new Date().toISOString(),
        }]);
      }

      if (response.action === 'create-request' && response.createdTicket) {
        const ticket = response.createdTicket;
        // Origin conversation is written by the backend — just clear local state
        setMessages([]);
        setPendingSuggestedRequest(null);
        setTicketCreated({ title: ticket.title, notionId: ticket.notionId });
        window.dispatchEvent(new CustomEvent('ticket-created'));
      }
    } catch {
      setMessages(prev => [...prev, {
        id:        msgId(),
        role:      'assistant',
        agent:     'oski',
        content:   'Tuve un problema al procesar tu mensaje. ¿Puedes intentarlo de nuevo?',
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
      setStatus('');
      inputRef.current?.focus();
    }
  };

  const resetToGreeting = () => {
    const name = teamUser?.displayName?.split(' ')[0] ?? '';
    setMessages([{
      id:        msgId(),
      role:      'assistant',
      agent:     'oski',
      content:   `Hola${name ? ` ${name}` : ''}! Soy Oski, el orquestador del equipo. Puedo ayudarte con tickets de producto, consultas legales, o conectarte con Pame o Dully según lo que necesites. ¿En qué te ayudo hoy?`,
      timestamp: new Date().toISOString(),
    }]);
    setActiveAgent('oski');
    setTicketCreated(null);
    setInput('');
    setPendingImages([]);
    setPendingSuggestedRequest(null);
    setSelectedApp(null);
  };

  const handleClear = () => {
    resetToGreeting();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items).filter(i => i.type.startsWith('image/'));
    if (!items.length) return;
    e.preventDefault();
    const files = items.map(i => i.getAsFile()).filter(Boolean) as File[];
    const attachments = await Promise.all(files.slice(0, MAX_IMAGES - pendingImages.length).map(readFileAsAttachment));
    if (attachments.length) setPendingImages(prev => [...prev, ...attachments].slice(0, MAX_IMAGES));
  };

  const semColor = { ok: 'var(--sem-ok)', degraded: 'var(--sem-warn)', error: 'var(--sem-err)' }[status.status];
  const semLabel = { ok: 'En línea', degraded: 'Latencia alta', error: 'No disponible' }[status.status];
  const agent    = AGENT_META[activeAgent];

  // Datos de la app seleccionada para el badge
  const selectedAppData = selectedApp ? apps.find(a => a.id === selectedApp) ?? null : null;

  useEffect(() => {
    if (loadingHistory || messages.length > 0) return;
    const name = teamUser?.displayName?.split(' ')[0] ?? '';
    setMessages([{
      id:        msgId(),
      role:      'assistant',
      agent:     'oski',
      content:   `Hola${name ? ` ${name}` : ''}! Soy Oski, el orquestador del equipo. Puedo ayudarte con tickets de producto, consultas legales, o conectarte con Pame o Dully según lo que necesites. ¿En qué te ayudo hoy?`,
      timestamp: new Date().toISOString(),
    }]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingHistory]);

  const canSend = !loading && !loadingHistory && status.status !== 'error' && (!!input.trim() || pendingImages.length > 0);

  return (
    <div className={styles.chat}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.agentAvatar} style={{ background: agent.gradient }}>
          {activeAgent.charAt(0).toUpperCase()}
        </div>
        <div className={styles.agentInfo}>
          <div className={styles.agentName}>{agent.label}</div>
          <div className={styles.agentStatus}>
            <span className={styles.statusDot} style={{
              background: semColor,
              animation: status.status === 'ok' ? 'pulse-ok 2.5s ease-in-out infinite' : 'none',
            }} />
            <span style={{ color: semColor, fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
              {checking ? 'Verificando...' : semLabel}
            </span>
          </div>
        </div>
        <div
          role="tablist"
          aria-label="Seleccionar agente"
          className={styles.agentTabs}
        >
          {VISIBLE_AGENTS.map(a => (
            <button
              key={a}
              role="tab"
              aria-selected={activeAgent === a}
              aria-controls="agent-panel"
              id={`tab-${a}`}
              tabIndex={activeAgent === a ? 0 : -1}
              onClick={() => setActiveAgent(a)}
              onKeyDown={(e) => {
                const idx = VISIBLE_AGENTS.indexOf(a);
                if (e.key === 'ArrowRight') {
                  const next = VISIBLE_AGENTS[(idx + 1) % VISIBLE_AGENTS.length];
                  setActiveAgent(next);
                  document.getElementById(`tab-${next}`)?.focus();
                } else if (e.key === 'ArrowLeft') {
                  const prev = VISIBLE_AGENTS[(idx - 1 + VISIBLE_AGENTS.length) % VISIBLE_AGENTS.length];
                  setActiveAgent(prev);
                  document.getElementById(`tab-${prev}`)?.focus();
                }
              }}
              className={`${styles.agentTab} ${activeAgent === a ? styles.agentTabActive : ''}`}
              style={{ '--tab-color': AGENT_META[a].color } as React.CSSProperties}>
              {a.charAt(0).toUpperCase() + a.slice(1)}
            </button>
          ))}
        </div>
        {messages.length > 1 && (
          <button
            className={styles.clearBtn}
            onClick={handleClear}
            disabled={loading}
            title="Nueva conversación"
            aria-label="Nueva conversación">
            <IconNewChat />
            <span className={styles.clearBtnLabel}>Nuevo chat</span>
          </button>
        )}
      </div>

      {/* Ticket creado — card de éxito */}
      {ticketCreated && (
        <div className={styles.ticketSuccess}>
          <div className={styles.ticketSuccessIcon}>✓</div>
          <div className={styles.ticketSuccessTitle}>Ticket creado</div>
          <div className={styles.ticketSuccessName}>{ticketCreated.title}</div>
          <p className={styles.ticketSuccessHint}>La conversación quedó guardada en el ticket.</p>
          <button className={styles.ticketSuccessBtn} onClick={resetToGreeting}>
            Nueva conversación →
          </button>
        </div>
      )}

      {/* Messages */}
      <div className={styles.messages} style={{ display: ticketCreated ? 'none' : undefined }}>
        {loadingHistory && (
          <>
            <HistorySkeleton />
            <HistorySkeleton wide />
            <HistorySkeleton />
          </>
        )}
        {!loadingHistory && messages.filter(msg => msg.content?.trim() || msg.images?.length).map(msg => (
          <div key={msg.id} className={`${styles.msg} ${msg.role === 'user' ? styles.user : ''}`}
            style={{ animation: 'fade-in 0.2s ease' }}>
            {msg.role === 'assistant' && (
              <div className={styles.msgAvatar}
                style={{ background: AGENT_META[msg.agent ?? 'oski'].gradient }}>
                {(msg.agent ?? 'oski').charAt(0).toUpperCase()}
              </div>
            )}
            <div className={styles.msgWrap}>
              {msg.role === 'assistant' && msg.agent && msg.agent !== 'oski' && (
                <div className={styles.agentTag} style={{ color: AGENT_META[msg.agent].color }}>
                  {AGENT_META[msg.agent].label}
                  {selectedAppData && (
                    <span className={styles.agentTagAppBadge} style={{ color: selectedAppData.color }}>
                      <span
                        className={styles.agentTagAppDot}
                        style={{ background: selectedAppData.color }}
                      />
                      {selectedAppData.label}
                    </span>
                  )}
                </div>
              )}
              {msg.role === 'assistant' && msg.agent === 'oski' && selectedAppData && (
                <div className={styles.agentTag} style={{ color: AGENT_META['oski'].color }}>
                  {AGENT_META['oski'].label}
                  <span className={styles.agentTagAppBadge} style={{ color: selectedAppData.color }}>
                    <span
                      className={styles.agentTagAppDot}
                      style={{ background: selectedAppData.color }}
                    />
                    {selectedAppData.label}
                  </span>
                </div>
              )}
              <div className={`${styles.bubble} ${msg.role === 'user' ? styles.userBubble : ''}`}>
                {msg.images?.length ? (
                  <div className={styles.msgImages}>
                    {msg.images.map((img, i) => (
                      <img
                        key={i}
                        src={`data:${img.mediaType};base64,${img.data}`}
                        className={styles.msgImage}
                        alt=""
                      />
                    ))}
                  </div>
                ) : null}
                {msg.role === 'assistant'
                  ? <MessageContent content={msg.content} />
                  : msg.content}
              </div>
              <div className={styles.time}>
                {new Date(msg.timestamp).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            {msg.role === 'user' && (
              <div className={styles.msgAvatar} style={{ background: 'var(--surface3)', border: '1px solid var(--border)' }}>
                {teamUser?.displayName?.[0]?.toUpperCase() ?? 'U'}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className={styles.msg}>
            <div className={styles.msgAvatar} style={{ background: agent.gradient }}>
              {activeAgent.charAt(0).toUpperCase()}
            </div>
            <div className={styles.bubble} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className={styles.typing}>
                <span /><span /><span />
              </div>
              {statusText && (
                <span style={{ fontSize: '12px', opacity: 0.6, fontStyle: 'italic' }}>
                  {statusText}
                </span>
              )}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* App chips — solo cuando hay un único mensaje de greeting */}
      {!ticketCreated && !loading && messages.length === 1 && apps.length > 0 && (
        <div className={styles.appChips}>
          {[
            ...apps.filter(a => {
              if (a.id === 'general' || (a.status ?? 'enabled') !== 'enabled') return false;
              if (isSysadmin) return true;
              const role: AppRole | undefined = teamUser?.appRoles?.[a.id as AppName];
              return role != null && role !== 'none';
            }),
            apps.find(a => a.id === 'general' && (a.status ?? 'enabled') === 'enabled')!,
          ]
            .filter(Boolean)
            .map(app => {
              const isActive = selectedApp === app.id;
              return (
                <button
                  key={app.id}
                  type="button"
                  className={`${styles.appChip} ${isActive ? styles.appChipActive : ''}`}
                  style={{ '--chipColor': app.color } as React.CSSProperties}
                  onClick={() => {
                    setSelectedApp(isActive ? null : app.id);
                    inputRef.current?.focus();
                  }}
                >
                  <span className={styles.appChipDot} style={{ background: app.color }} />
                  {app.label}
                </button>
              );
            })}
        </div>
      )}

      {/* Input */}
      <form className={styles.inputArea} onSubmit={send} style={{ display: ticketCreated ? 'none' : undefined }}>
        {selectedApp && messages.length === 1 && (
          <div className={styles.selectedAppBadge}>
            <span style={{ background: apps.find(a => a.id === selectedApp)?.color ?? 'var(--accent)', width: 6, height: 6, borderRadius: '50%', display: 'inline-block' }} />
            {apps.find(a => a.id === selectedApp)?.label ?? selectedApp}
            <button type="button" className={styles.selectedAppBadgeX} onClick={() => setSelectedApp(null)}>×</button>
          </div>
        )}
        {pendingImages.length > 0 && (
          <div className={styles.imagePreview}>
            {pendingImages.map((img, i) => (
              <div key={i} className={styles.imageThumb}>
                <img src={`data:${img.mediaType};base64,${img.data}`} alt="" />
                <button
                  type="button"
                  className={styles.imageThumbRemove}
                  onClick={() => removeImage(i)}
                  aria-label="Quitar imagen"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        <div className={styles.inputRow}>
          <button
            type="button"
            className={styles.attachBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || loadingHistory || pendingImages.length >= MAX_IMAGES}
            title="Adjuntar imagen"
            aria-label="Adjuntar imagen"
          >
            <IconPaperclip />
          </button>
          <textarea
            ref={inputRef}
            className={styles.input}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="Escríbele a Oski — tickets, consultas legales, desarrollo..."
            rows={2}
            disabled={loading || loadingHistory || status.status === 'error'}
          />
          <button
            type="submit"
            className={styles.sendBtn}
            disabled={!canSend}>
            <IconSend />
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </form>
    </div>
  );
}

// ── MessageContent: colapsa tool_calls y bloques json ───────────────────────

type Segment =
  | { type: 'text'; content: string }
  | { type: 'tool_calls'; calls: string[]; count: number }
  | { type: 'json'; content: string };

function parseContent(raw: string): Segment[] {
  const segments: Segment[] = [];
  // Detecta: ```<tool_call>…</tool_call>```, <tool_call>…</tool_call>, ```json…```
  const pattern = /(```\s*\n?<tool_call>[\s\S]*?<\/tool_call>\s*\n?```|<tool_call>[\s\S]*?<\/tool_call>|```json[\s\S]*?```)/g;
  let last = 0;
  let toolBuf: string[] = [];

  const flushTools = () => {
    if (toolBuf.length) {
      segments.push({ type: 'tool_calls', calls: [...toolBuf], count: toolBuf.length });
      toolBuf = [];
    }
  };

  let m: RegExpExecArray | null;
  while ((m = pattern.exec(raw)) !== null) {
    const before = raw.slice(last, m.index);
    if (before.trim()) { flushTools(); segments.push({ type: 'text', content: before }); }

    if (m[0].includes('<tool_call>')) {
      // Extraer el JSON interno del tool_call
      const inner = m[0].replace(/^```\s*\n?/, '').replace(/\n?```$/, '').trim();
      toolBuf.push(inner);
    } else {
      flushTools();
      const jsonContent = m[0].replace(/^```json\s*/, '').replace(/\s*```$/, '').trim();
      segments.push({ type: 'json', content: jsonContent });
    }
    last = m.index + m[0].length;
  }
  flushTools();
  const tail = raw.slice(last);
  if (tail.trim()) segments.push({ type: 'text', content: tail });
  return segments;
}

function MessageContent({ content }: { content: string }) {
  const segments = parseContent(content);
  if (segments.length === 1 && segments[0].type === 'text') {
    return <span className={styles.msgText}>{content}</span>;
  }
  return (
    <div className={styles.msgContent}>
      {segments.map((seg, i) => {
        if (seg.type === 'text') {
          return <span key={i} className={styles.msgText}>{seg.content.replace(/^\n+|\n+$/g, '')}</span>;
        }
        if (seg.type === 'tool_calls') {
          return (
            <details key={i} className={styles.collapsible}>
              <summary className={styles.collapsibleSummary}>
                <IconToolCall />
                {seg.count} llamada{seg.count > 1 ? 's' : ''} de herramienta
                <span className={styles.collapsibleChevron}>▸</span>
              </summary>
              <div className={styles.collapsibleContent}>
                {seg.calls.map((call, j) => (
                  <pre key={j} className={styles.collapsiblePre}>{call}</pre>
                ))}
              </div>
            </details>
          );
        }
        if (seg.type === 'json') {
          let parsed = seg.content;
          try { parsed = JSON.stringify(JSON.parse(seg.content), null, 2); } catch { /* keep raw */ }
          return (
            <details key={i} className={styles.collapsible}>
              <summary className={styles.collapsibleSummary}>
                <IconJson />
                Datos técnicos
                <span className={styles.collapsibleChevron}>▸</span>
              </summary>
              <div className={styles.collapsibleContent}>
                <pre className={styles.collapsiblePre}>{parsed}</pre>
              </div>
            </details>
          );
        }
        return null;
      })}
    </div>
  );
}

function IconToolCall() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
    </svg>
  );
}

function IconJson() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
    </svg>
  );
}

function IconSend() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
      <path d="M2 2l12 6-12 6V9.5l8-1.5-8-1.5V2z"/>
    </svg>
  );
}

function IconNewChat() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14"/>
    </svg>
  );
}

function IconPaperclip() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
    </svg>
  );
}

function HistorySkeleton({ wide = false }: { wide?: boolean }) {
  return (
    <div className={styles.msg} aria-hidden="true">
      <div className={styles.msgAvatar} style={{ background: 'var(--surface2)', border: '1px solid var(--border)' }} />
      <div className={styles.msgWrap}>
        <div className={styles.skeletonBubble} style={{ width: wide ? '72%' : '48%' }} />
      </div>
    </div>
  );
}
